import { gql, useQuery } from "@apollo/client";
import { addAuthVars } from "@teamrota/authlib";
import { update } from "lodash/fp";
import moment from "moment";
import useAuth from "~/src/auth/hooks/use-auth";

export const GET_SCHEDULES = addAuthVars(gql`
  query getSchedules(
    $startTime: Date!
    $endTime: Date!
    $roleIds: [ID]
    $sortBy: SortByType
    $searchTerm: String
    $homeVenueIds: [ID]
    $venueIds: [ID]
    $userIds: [ID]
    $offset: Int
    $limit: Int
    $partnerIds: [ID]
    $isShowCancelledShifts: Boolean
    $selectedMemberTypes: [String]
    $selectedMemberInfo: [String]
  ) {
    account {
      id
      isMemberAvailabilityEnabled
      schedule(
        startTime: $startTime
        endTime: $endTime
        bookingStates: [ACCEPTED]
        roleIds: $roleIds
        sortBy: $sortBy
        searchTerm: $searchTerm
        homeVenueIds: $homeVenueIds
        venueIds: $venueIds
        userIds: $userIds
        offset: $offset
        limit: $limit
        partnerIds: $partnerIds
        isShowCancelledShifts: $isShowCancelledShifts
        selectedMemberTypes: $selectedMemberTypes
        selectedMemberInfo: $selectedMemberInfo
      ) {
        days
        draftShiftIds
        totalMembersCount
        scheduledMembers {
          memberId
          member {
            id
            firstName
            lastName
            photo
            memberType
            venueId
            isLimitedHours
            limitedHours
            roles {
              roleId
              isDayRate
            }
          }
          scheduledDays {
            startOfDay
            bookings {
              id
              state
              shiftId
              shift {
                id
                startTime
                endTime
                name
                cancelledAt
                sourceAccountId
                shiftGroupId
                sourceAccount {
                  accountName
                }
                isDraft
                type
                venue {
                  id
                  address
                  name
                }
                ... on shift {
                  payRate
                  roleRate {
                    id
                    roleName
                    roleId

                    ... on roleRate {
                      payRate
                    }
                  }
                }
              }
            }
            availability {
              startsAt
              endsAt
              shiftType
            }
            unavailability {
              startsAt
              endsAt
            }
          }
          hoursCompleted
        }
        unassignedShifts {
          date
          shifts {
            id
            type
            startTime
            numberRequested
            endTime
            isDraft
            name
            sourceAccountId
            targetAccountId
            sourceAccount {
              accountName
            }
            shiftGroupId
            venue {
              id
              address
              name
            }
            ... on shift {
              payRate
              roleRate {
                id
                roleName
                roleId

                ... on roleRate {
                  payRate
                }
              }
            }

            cancelledAt

            bookings(stateIn: [ACCEPTED], offset: 0, limit: 100) {
              totalResults
            }
          }
        }
      }
    }
  }
`);

export const useSchedules = ({
  startTime,
  endTime,
  homeVenueIds,
  roleIds,
  sortBy,
  searchTerm,
  partnerIds,
  venueIds,
  userIds,
  isShowCancelledShifts,
  selectedMemberTypes,
  selectedMemberInfo,
  skip
}) => {
  const auth = useAuth();

  const {
    data: scheduleData,
    refetch: refetchSchedules,
    fetchMore,
    loading: schedulesLoading
  } = useQuery(GET_SCHEDULES, {
    variables: auth.addVals(GET_SCHEDULES, {
      startTime: moment(startTime)
        .local()
        .format(),
      endTime: moment(endTime)
        .endOf("day")
        .local()
        .format(),
      homeVenueIds,
      roleIds,
      sortBy,
      searchTerm,
      partnerIds,
      venueIds,
      userIds,
      isShowCancelledShifts,
      selectedMemberTypes,
      selectedMemberInfo
    }),
    fetchPolicy: "cache-and-network",
    skip,
    onError: error => {
      throw error;
    }
  });

  const loadMoreSchedules = () =>
    fetchMore({
      variables: {
        offset: scheduleData.account.schedule.scheduledMembers.length,
        limit: 25
      },
      updateQuery: (previousResult, { fetchMoreResult }) =>
        update("account.schedule.scheduledMembers", current => [
          ...current,
          ...fetchMoreResult.account.schedule.scheduledMembers
        ])(previousResult)
    });

  return {
    scheduleData,
    refetchSchedules,
    loadMoreSchedules,
    schedulesLoading
  };
};
